/**
 * The Organization Overview -- the whole hierarchy the caller may see, on one
 * page, in four layouts (tree table, columns, card drill-down, block chart).
 *
 * ONE request (`GET /organization/overview`) feeds every layout. The server
 * narrows it to the caller's scope, attaches owners and counts, and sends
 * context ancestors flagged `in_scope = false` where a caller is named below
 * the top; the layouts draw that tree and never decide what may be shown.
 * Search and the active-only switch run client-side over the loaded tree,
 * exactly like the list screens: the endpoint takes no parameter.
 *
 * The chosen layout is remembered per signed-in user in `localStorage`, behind
 * a guard -- a blocked store simply means the default layout every visit.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Building2, RotateCw, Search, ShieldAlert } from 'lucide-react';

import { useOrganizationOverview } from '@/api/queries';
import { isRefusal } from '@/routes/business-unit-groups/groupView';
import { useSessionScope } from '@/session/SessionScopeContext';
import { ScopedEmptyState } from '@/ui/ScopedEmptyState';
import '@/styles/organization-overview.css';

import { BlocksView } from './BlocksView';
import { CardsView } from './CardsView';
import { ColumnsView } from './ColumnsView';
import { DetailPanel } from './DetailPanel';
import { TreeTableView } from './TreeTableView';
import {
  VIEWS,
  buildTree,
  filterTree,
  isFiltering,
  loadView,
  saveView,
  type ViewId,
} from './overviewModel';
import type { ViewProps } from './viewProps';

const LAYOUTS: Readonly<Record<ViewId, (props: ViewProps) => JSX.Element>> = {
  tree: TreeTableView,
  columns: ColumnsView,
  cards: CardsView,
  blocks: BlocksView,
};

function PageHead() {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Organization overview</h1>
        <div className="page-sub">
          Who owns delivery and sales at every level, from Business Unit Group to Engagement
        </div>
      </div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="card" aria-busy="true" aria-label="Loading the organization">
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

export function OrganizationOverviewPage() {
  const overview = useOrganizationOverview();
  const { user } = useSessionScope();
  const userId = user?.id ?? null;

  const [view, setView] = useState<ViewId>(() => loadView(userId));
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The session may resolve after first paint: pick up that user's own layout.
  useEffect(() => {
    setView(loadView(userId));
  }, [userId]);

  const chooseView = useCallback(
    (next: ViewId) => {
      setView(next);
      setSelectedId(null);
      saveView(userId, next);
    },
    [userId],
  );

  const tree = useMemo(
    () => (overview.data ? buildTree(overview.data) : null),
    [overview.data],
  );
  const filter = useMemo(() => ({ search, activeOnly }), [search, activeOnly]);
  const roots = useMemo(() => (tree ? filterTree(tree.roots, filter) : []), [tree, filter]);
  // Context ancestors and engagements alone draw nothing: a caller with no
  // in-scope group, unit or client (named nowhere, or a project head who typed
  // the URL) gets the scoped empty state, with no header counts and no views.
  const hasScopedNode = useMemo(
    () => (tree ? [...tree.byId.values()].some((n) => n.inScope && n.level !== 'engagement') : false),
    [tree],
  );

  // Escape closes the overlay panel (the docked one has a close button only).
  useEffect(() => {
    if (!selectedId || view === 'columns') return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedId, view]);

  if (overview.isPending) return <LoadingSkeleton />;

  if (overview.error || !tree) {
    const failure = overview.error;
    // A 403 is a refusal, not a failure, and never retried.
    if (isRefusal(failure)) {
      return (
        <>
          <PageHead />
          <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="No access">
            <p>The server refuses this area for your account, and records the refusal in the audit log.</p>
          </StateCard>
        </>
      );
    }
    return (
      <>
        <PageHead />
        <StateCard warning icon={<AlertTriangle aria-hidden="true" />} title="The organization could not be loaded">
          <p>{failure instanceof Error ? failure.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button type="button" className="btn btn-secondary" onClick={() => void overview.refetch()}>
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  if (!hasScopedNode) {
    return (
      <>
        <PageHead />
        <div className="card">
          <ScopedEmptyState
            searching={false}
            matchNoun="nodes"
            collectionNoun="Business Unit Groups"
            icon={<Building2 aria-hidden="true" />}
            freshInstallHint="Wait for the master workbook import to bring the hierarchy across, or create the first group."
          />
        </div>
      </>
    );
  }

  const filtering = isFiltering(filter);
  const Layout = LAYOUTS[view];
  const selected = selectedId ? (tree.byId.get(selectedId) ?? null) : null;
  const docked = view === 'columns';
  const { totals } = tree;
  const stats: [number, string][] = [
    [tree.roots.length, 'Groups'],
    [totals.business_units, 'Business Units'],
    [totals.clients, 'Clients'],
    [totals.engagements, 'Engagements'],
    [totals.high_risk, 'High-risk engagements'],
  ];

  return (
    <>
      <PageHead />

      <div className="ov-stats" aria-label="Totals in your scope">
        {stats.map(([value, label]) => (
          <div className="ov-stat" key={label}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <div className="ov-seg" role="tablist" aria-label="Layout">
          {VIEWS.map((option, index) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={view === option.id}
              className="ov-seg-btn"
              onClick={() => chooseView(option.id)}
            >
              <span className="k" aria-hidden="true">{index + 1}</span>
              {option.label}
            </button>
          ))}
        </div>
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search name, code or owner"
            aria-label="Search name, code or owner"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <label className="ov-check">
          <input type="checkbox" checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />
          Active only
        </label>
      </div>

      {roots.length === 0 ? (
        <div className="card">
          <ScopedEmptyState
            searching
            matchNoun="nodes"
            collectionNoun="Business Unit Groups"
            icon={<Building2 aria-hidden="true" />}
            freshInstallHint="Wait for the master workbook import to bring the hierarchy across, or create the first group."
          />
        </div>
      ) : (
        <div className={docked && selected ? 'ov-split' : undefined}>
          <Layout
            roots={roots}
            tree={tree}
            selectedId={selectedId}
            onSelect={setSelectedId}
            filtering={filtering}
          />
          {selected && docked && (
            <DetailPanel node={selected} tree={tree} docked onClose={() => setSelectedId(null)} onSelect={setSelectedId} />
          )}
        </div>
      )}

      {selected && !docked && (
        <>
          <div className="ov-scrim" onClick={() => setSelectedId(null)} aria-hidden="true" />
          <DetailPanel node={selected} tree={tree} docked={false} onClose={() => setSelectedId(null)} onSelect={setSelectedId} />
        </>
      )}
    </>
  );
}
